import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Input } from "@/components/ui/input/Input";
import { Label } from "@/components/ui/label/Label";
import ManagementService from "@/lib/services/management/ManagementService";
import { IRole } from "@/models/management/Role";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (formData: any) => void;
};

export function AddPICDialog({ isOpen, onClose, onConfirm }: Props) {
  const [roles, setRoles] = useState<IRole[]>([]);
  const [form, setForm] = useState({
    initial: "",
    name: "",
    email: "",
    password: "",
    role_id: ""
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const data = await ManagementService.getRoles();
        const rolesData = Array.isArray(data) ? data : [];
        setRoles(rolesData);
        
        if (rolesData.length > 0) {
          setForm(prev => ({ ...prev, role_id: rolesData[0].id.toString() }));
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(form);
    setForm({ 
      initial: "", 
      name: "", 
      email: "", 
      password: "", 
      role_id: roles[0]?.id.toString() || "" 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-card border-border font-lregular">
        <DialogHeader>
          <DialogTitle className="font-lsemibold text-foreground">Assign New PIC</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Initial</Label>
              <Input required value={form.initial} onChange={e => setForm({...form, initial: e.target.value})} placeholder="CY25-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Role</Label>
              <select 
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                value={form.role_id}
                onChange={e => setForm({...form, role_id: e.target.value})}
                required
              >
                <option value="" disabled>Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Full Name</Label>
            <Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Email</Label>
            <Input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="name@binus.edu" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Password</Label>
            <Input required type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" />
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="blue">Assign PIC</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}